<template>
  <div>
    <loading
      :active.sync="isLoading"
      :is-full-page="fullPageLoader"
      :color="loaderColor"
    ></loading>
    <div 
      :style="playerColor" 
      class="groovevideo-video-container"
    >
      <video-by-link
        v-if="fetchedVideo"
        :key="videoPlayerComponentKey"
        :options="videoOptions"
        class="video-js vjs-big-play-centered"
        :class="theme"
        :vid="vid"
      ></video-by-link>
    </div>
  </div>
</template>

<script type="text/javascript">
import 'videojs-watermark';
import 'videojs-overlay/dist/videojs-overlay.js';
import 'videojs-overlay/dist/videojs-overlay.css';
import Loading from 'vue-loading-overlay';

import VideoByLink from '@groovepages/gd-ui-kit/components/video-player/VideoByLink.vue';
import themeConfig from '../../theme/themeConfig.js';
import Video from '../../models/Video';

export default {
  components: {
    VideoByLink,
    Loading,
  },
  layout: 'basic',
  data() {
    return {
      fetchedVideo: 0,
      playerColor: {},
      plink: '',
      videoPlayerComponentKey: null,
      embedded_video: {},
      fullPageLoader: true,
      isLoading: false,
      loaderColor: themeConfig.appPrimaryColor,
      text_overlay: '',
      video_end: false,
      vid: {},
      videoOptions: {
        showControls: 1,
        controls: true,
        controlBar: {
          playToggle: true,
          captionsButton: false,
          chaptersButton: false,
          subtitlesButton: false,
          remainingTimeDisplay: true,
          progressControl: {
            seekBar: true,
          },
          fullscreenToggle: false,
          playbackRateMenuButton: false,
          pictureInPictureToggle: true,
          volumePanel: true,
          muteToggle: false,
        },
        autoplay: true,
        muted: false,
        poster: '',
        sources: [
          {
            src: '',
            type: 'video/mp4',
          },
        ],
        loop: false,
        userActions: {
          doubleClick: true,
        },
      },
    };
  },
  computed: {
    theme() {
      if(this.vid.settings.theme == null || this.vid.settings.theme == " ") {
        if(this.vid.video_source_type === 1) {
          return 'groovevideo-video-wrapper vjs-theme-default';
        }
        return 'vjs-theme-default';
      } else if(this.vid.settings.theme != " ") {
          return this.vid.settings.theme;
      }
    }
  },
  async mounted() {},
  async created() {
    let permalink = this.$route.params.permalink;
    let videoId = this.$route.params.id;

    this.$wait.start('videos');
    this.vid = await Video.params({
      permalink: permalink,
      id: videoId,
    })
      .custom('fetch-with-permalink')
      .$first();

    this.overrideVideoSettings();

    this.setColors();

    if (!this.vid.settings.control) {
      this.videoOptions.showControls = 0;
      this.videoOptions.controlBar.playToggle = false;
      this.videoOptions.controlBar.remainingTimeDisplay = false;
      this.videoOptions.controlBar.progressControl.seekBar = false;
      this.videoOptions.controlBar.fullscreenToggle = false;
      this.videoOptions.controlBar.pictureInPictureToggle = false;
      this.videoOptions.controlBar.volumePanel = false;
      this.videoOptions.controlBar.muteToggle = false;
    }

    // Autoplay settings
    if (this.vid.settings.autoplay) {
      this.videoOptions.autoplay = 'muted';
    } else {
      this.videoOptions.autoplay = false;
    }

    this.videoOptions.muted = this.vid.settings.muted;
    this.videoOptions.poster = this.vid.settings.splashImg;

    this.fetchedVideo = 1;
    this.$wait.end('videos');
  },
  methods: {
    setColors() {
      this.playerColor =
        typeof this.vid.settings !== 'undefined' &&
        typeof this.vid.settings.color !== 'undefined' &&
        typeof this.vid.settings.color.primary !== 'undefined' &&
        typeof this.vid.settings.color.secondary !== 'undefined'
          ? {
              '--player-primary-color': this.vid.settings.color.primary,
              '--player-secondary-color': this.vid.settings.color.secondary,
            }
          : {
              '--player-primary-color': 'rgba(253, 100, 129, 1)',
              '--player-secondary-color': 'rgba(255,255,255, 1)',
            };

      this.playerColor['--player-width'] = this.vid.video_source_type === 1 && !this.vid.dimension_percentage ? 'auto' : '100%';
      this.playerColor['--padding'] = this.vid.dimension_percentage ? `${this.vid.dimension_percentage}%` : '';
    },

    overrideVideoSettings() {
      let newSettings = null;

      if(this.$route.query.settings !== undefined) {
        try {
          newSettings = JSON.parse(decodeURIComponent(this.$route.query.settings.replace(/\+/g, ' ')));
        } catch(e) {
          console.error('Error in Settings object',e);
        }
      }

      if(!newSettings || newSettings === null) return;
      
      if(newSettings.video_dimensions && (newSettings.video_dimensions.type === 1 || 
        newSettings.video_dimensions.type === 2 && typeof newSettings.video_dimensions.width === 'number' && typeof newSettings.video_dimensions.height === 'number')) {
          this.vid.video_dimensions = { ...newSettings.video_dimensions, ...(this.vid.video_dimensions?.format_version === 1 ? { format_version: 1, aspect_ratio: this.vid.video_dimensions.aspect_ratio } : {}) };
      }

      this.vid.end_actions = newSettings.end_actions ? newSettings.end_actions : this.vid.end_actions;

      this.videoSettings(newSettings);
      this.setAutoplay(newSettings.autoplay_settings);
      this.setPlayButton(newSettings.play_button);
    },

    // eslint-disable-next-line complexity
    videoSettings(newSettings) {
      this.vid.settings = {
        ...this.vid.settings,
        color: {
          primary: newSettings.color && newSettings.color.primary ? newSettings.color.primary : this.vid.settings.color.primary,
          secondary: newSettings.color && newSettings.color.secondary ? newSettings.color.secondary : this.vid.settings.color.secondary,
        },
        muted: this.checkBoolean(newSettings.muted) ? newSettings.muted : this.vid.settings.muted,
        control: this.checkBoolean(newSettings.control) ? newSettings.control : this.vid.settings.control,
        autoplay: this.checkBoolean(newSettings.autoplay) ? newSettings.autoplay : this.vid.settings.autoplay,
        ytControls: this.checkBoolean(newSettings.ytControls) ? newSettings.ytControls : this.vid.settings.ytControls,
        theme: newSettings.theme ? newSettings.theme : this.vid.settings.theme,
        watermark: {
          source: newSettings.watermark && newSettings.watermark.source && newSettings.watermark.source !== '' ? newSettings.watermark.source : this.vid.settings.watermark?.source,
          position: newSettings.watermark && newSettings.watermark.position && ['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(newSettings.watermark.position) ? newSettings.watermark.position : this.vid.settings.watermark?.position,
        }
      };
    },

    // eslint-disable-next-line complexity
    setAutoplay(autoplaySettings) {
      if(!this.vid.settings.autoplay || !autoplaySettings) return;

      const newAutoplaySettings = this.vid.settings.autoplay_settings;
      
      this.vid.settings.autoplay_settings.begin_again = this.checkBoolean(autoplaySettings.begin_again) ? autoplaySettings.begin_again : newAutoplaySettings.begin_again;
      this.vid.settings.autoplay_settings.show_illustration = this.checkBoolean(autoplaySettings.show_illustration) ? autoplaySettings.show_illustration : newAutoplaySettings.show_illustration;

      if(!newAutoplaySettings.show_illustration) return;

      this.vid.settings.autoplay_settings.type = autoplaySettings.type && [1,2].includes(autoplaySettings.type) ? autoplaySettings.type : newAutoplaySettings.type;
      this.vid.settings.autoplay_settings.custom_image =  autoplaySettings.custom_image ? autoplaySettings.custom_image : newAutoplaySettings.custom_image;
      this.vid.settings.unmute_size = autoplaySettings.unmute_size && [1,2,3,4].includes(autoplaySettings.unmute_size) ? autoplaySettings.unmute_size : this.vid.settings.unmute_size;
      this.vid.settings.unmute_image =  autoplaySettings.unmute_image ? autoplaySettings.unmute_image : this.vid.settings.unmute_image;
      this.vid.settings.unmute_animation = autoplaySettings.unmute_animation ? autoplaySettings.unmute_animation : this.vid.settings.unmute_animation;
    },

    // eslint-disable-next-line complexity
    setPlayButton(playButton) {
      if(!playButton) return;
  
      const newPlayButton = this.vid.play_button;
      this.vid.play_button.enable = this.checkBoolean(playButton.enable) ? playButton.enable : newPlayButton.enable;
      
      if(!this.vid.play_button.enable) return;

      this.vid.play_button = {
        ...this.vid.play_button,
        type: playButton.type && [1,2,3].includes(playButton.type) ? playButton.type : newPlayButton.type,
        library_source: playButton.library_source ? playButton.library_source : newPlayButton.library_source,
        custom_source: playButton.custom_source ? playButton.custom_source : newPlayButton.custom_source,
        size: playButton.size && [1,2,3,4].includes(playButton.size) ? playButton.size : newPlayButton.size,
        animation: playButton.animation ? playButton.animation : newPlayButton.animation,
        show_on_pause: this.checkBoolean(playButton.show_on_pause) ? playButton.show_on_pause : newPlayButton.show_on_pause
      };
    },

    checkBoolean(newSetting) {
      return newSetting !== undefined && (typeof newSetting === 'boolean' || [0,1].includes(newSetting));
    }
  },
  
};
</script>
<style>
.vjs-control-bar,
.optin-form-cta-contaner .cta-optin-form-submit-button,
.optin-form-cta-contaner .cta-optin-form-submit-button:hover,
.optin-form-cta-contaner .cta-optin-form-submit-button:active,
.optin-form-cta-contaner .cta-optin-form-submit-button:focus,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button:hover,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button:active {
  background-color: var(--player-primary-color) !important;
}
.cta-skip-button:hover,
.banner-skip:hover {
  color: var(--player-primary-color) !important;
}
.video-js {
  color: var(--player-secondary-color) !important;
}
</style>
<style scoped>
.groovevideo-video-container {
  overflow: hidden;
  width: 100%;
  background-color: transparent;
}
.vjs-control-bar,
.optin-form-cta-contaner .cta-optin-form-submit-button,
.optin-form-cta-contaner .cta-optin-form-submit-button:hover,
.optin-form-cta-contaner .cta-optin-form-submit-button:active,
.optin-form-cta-contaner .cta-optin-form-submit-button:focus,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button:hover,
.caption-cta-button-container.cta-overlay .caption-cta-caption-button:active {
  background-color: var(--player-primary-color) !important;
}
.cta-skip-button:hover,
.banner-skip:hover {
  color: var(--player-primary-color) !important;
}
.video-js {
  color: var(--player-secondary-color) !important;
}
.img {
  width: 50px !important;
  position: relative;
  right: -300px;
  opacity: 0.7;
  cursor: pointer;
}
</style>
